import { Fragment, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Correcciones from "@/pages/Correcciones";
import { ChevronDown, ChevronRight } from "lucide-react";

const FIELD_LABELS = {
  operation_type: "Operacion", market_type: "Mercado", currency: "Moneda",
  usdt_amount: "Monto USDT", rate_dop: "Precio DOP", percentage: "Porcentaje",
  total_amount: "Total", total_currency: "Moneda del total", client_id: "Cliente",
  referrer_id: "Referidor", memo: "Memo", transaction_date: "Fecha y hora",
  status: "Estado",
};

const displayedValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "Sin valor";
  if (field === "transaction_date") return fmt.date(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const changesFor = (entry) => {
  const before = entry.previous_data || entry.before || {};
  const after = entry.new_data || entry.after || {};
  if (entry.action === "annul" && !Object.keys(after).length) {
    return [{ field: "status", before: before.status || "activa", after: "anulada" }];
  }
  return Object.keys(FIELD_LABELS)
    .filter((field) => JSON.stringify(before[field] ?? null) !== JSON.stringify(after[field] ?? null))
    .map((field) => ({ field, before: before[field], after: after[field] }));
};

function HistorialAuditoria() {
  const [logs, setLogs] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { api.get("/audit").then(({ data }) => setLogs(data)).catch(() => {}); }, []);

  return (
    <Card className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Fecha</th>
              <th className="px-4 py-3 text-left font-semibold">Usuario</th>
              <th className="px-4 py-3 text-left font-semibold">Accion</th>
              <th className="px-4 py-3 text-left font-semibold">Transaccion</th>
              <th className="px-4 py-3 text-left font-semibold">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Sin registros de auditoria</td></tr>}
            {logs.map((entry) => {
              const isExpanded = expanded === entry.id;
              const changes = changesFor(entry);
              return (
                <Fragment key={entry.id}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${entry.id}`}>
                    <td className="px-4 py-3 text-slate-600 font-mono tabular-nums">{fmt.date(entry.change_date)}</td>
                    <td className="px-4 py-3 text-slate-800">{entry.changed_by_name}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${entry.action === "annul" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{entry.action === "annul" ? "Anulacion" : "Edicion"}</span></td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs"><button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => setExpanded(isExpanded ? null : entry.id)} aria-expanded={isExpanded}>{isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{entry.transaction_id?.slice(0, 8)}...</button></td>
                    <td className="px-4 py-3 text-slate-700">{entry.edit_reason}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50" data-testid={`audit-detail-${entry.id}`}>
                      <td colSpan={5} className="px-5 py-4">
                        <div className="grid gap-2">
                          {changes.length === 0 && <p className="text-sm text-slate-500">Este registro antiguo no contiene diferencias estructuradas.</p>}
                          {changes.map((change) => (
                            <div key={change.field} className="grid sm:grid-cols-[160px_1fr_24px_1fr] gap-2 items-center text-sm border-b border-slate-200 pb-2">
                              <span className="font-semibold text-slate-700">{FIELD_LABELS[change.field]}</span>
                              <span className="text-rose-700 break-words">{displayedValue(change.field, change.before)}</span>
                              <span className="text-center text-slate-400">-&gt;</span>
                              <span className="text-emerald-700 break-words">{displayedValue(change.field, change.after)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Auditoria() {
  return (
    <div className="space-y-6" data-testid="auditoria-page">
      <div><h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Auditoria</h1><p className="text-slate-500 mt-1">Solicitudes de correccion e historial de cambios</p></div>
      <Tabs defaultValue="correcciones">
        <TabsList className="bg-white border border-slate-200"><TabsTrigger value="correcciones" data-testid="tab-correcciones">Correcciones</TabsTrigger><TabsTrigger value="historial" data-testid="tab-historial">Historial</TabsTrigger></TabsList>
        <TabsContent value="correcciones" className="mt-4"><Correcciones embedded /></TabsContent>
        <TabsContent value="historial" className="mt-4"><HistorialAuditoria /></TabsContent>
      </Tabs>
    </div>
  );
}
